import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, Button, Chip, Grid, Card, CardContent,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  LinearProgress, Alert, IconButton, Tooltip, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, FormControlLabel, Switch, Collapse,
} from '@mui/material';
import {
  Tune, Add, Edit, Delete, Close, Lock, PlayArrow, ExpandMore, ExpandLess,
  Star, Warning, TrendingUp, People,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

function apiFetch(path: string, opts: RequestInit = {}) {
  return fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:4000'}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}`, ...(opts.headers || {}) },
  }).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.message || 'Error'); return d; });
}

// ── Constants ──────────────────────────────────────────────────────────────────

const RATINGS: Record<string, { label: string; color: string; bg: string; score: number }> = {
  exceptional:        { label: 'Exceptional',        color: '#fff', bg: '#1565c0', score: 5 },
  exceeds:            { label: 'Exceeds',            color: '#fff', bg: '#2e7d32', score: 4 },
  meets:              { label: 'Meets',              color: '#fff', bg: '#558b2f', score: 3 },
  below_expectations: { label: 'Below Expectations', color: '#fff', bg: '#e65100', score: 2 },
  unsatisfactory:     { label: 'Unsatisfactory',     color: '#fff', bg: '#c62828', score: 1 },
};

const RATING_ORDER = ['exceptional', 'exceeds', 'meets', 'below_expectations', 'unsatisfactory'];

const CYCLES: Record<string, string> = {
  year_end: 'Year-End', mid_year: 'Mid-Year', q1: 'Q1', q2: 'Q2', q3: 'Q3', q4: 'Q4',
};

const STATUS_COLORS: Record<string, any> = { open: 'success', in_review: 'warning', locked: 'default' };
const STATUS_LABELS: Record<string, string> = { open: 'Open', in_review: 'In Review', locked: 'Locked' };

function RatingChip({ rating }: { rating?: string }) {
  if (!rating) return <Chip label="—" size="small" />;
  const r = RATINGS[rating];
  return <Chip label={r?.label ?? rating} size="small" sx={{ bgcolor: r?.bg, color: r?.color, fontWeight: 700, fontSize: '0.7rem' }} />;
}

function fmtDate(s: string) {
  return new Date(s + 'T00:00:00').toLocaleDateString('en-HK', { day: 'numeric', month: 'short', year: 'numeric' });
}

// ── Types ──────────────────────────────────────────────────────────────────────

interface Session {
  id: string; name: string; cycle: string; year: number; department?: string;
  openDate: string; closeDate: string; status: string;
  targetExceptional: number; targetExceeds: number; targetMeets: number;
  targetBelowExpectations: number; targetUnsatisfactory: number;
  facilitatorNotes?: string; lockedAt?: string; entries?: Entry[];
}

interface Entry {
  id: string; sessionId: string; employeeId: string;
  managerRating?: string; calibratedRating?: string;
  proposedIncreasePercent?: number; approvedIncreasePercent?: number;
  bonusMultiplier?: number;
  isHighPotential: boolean; isAtRisk: boolean; isSuccessionCandidate: boolean;
  discussionNotes?: string; managerJustification?: string; isLocked: boolean;
  employee?: { id: string; firstName: string; lastName: string; position?: string; department?: { name: string } };
}

interface Distribution {
  total: number;
  byRating: Record<string, { count: number; percent: number }>;
  targetMap: Record<string, number>;
  highPotential: number; atRisk: number; successionCandidates: number;
  avgIncreasePercent: number;
}

// ── Sub-components ──────────────────────────────────────────────────────────────

function DistributionBar({ rating, actual, target, count, total }: any) {
  const r = RATINGS[rating];
  const diff = actual - target;
  return (
    <Box sx={{ mb: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
        <Typography variant="caption" fontWeight={600}>{r?.label ?? rating}</Typography>
        <Typography variant="caption" color={diff > 5 ? 'error' : diff < -5 ? 'warning.main' : 'text.secondary'}>
          {count} ({actual}%) {target > 0 && `· target ${target}%`}
        </Typography>
      </Box>
      <Box sx={{ position: 'relative', height: 12, bgcolor: '#f0f0f0', borderRadius: 1, overflow: 'visible' }}>
        <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${Math.min(actual, 100)}%`, bgcolor: r?.bg ?? '#90a4ae', borderRadius: 1, transition: 'width 0.4s' }} />
        {target > 0 && (
          <Box sx={{ position: 'absolute', top: -2, bottom: -2, left: `${target}%`, width: 2, bgcolor: '#333', borderRadius: 1 }} />
        )}
      </Box>
    </Box>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────

export default function CalibrationPage() {
  const { user } = useAuth();
  const isHR = ['hr_admin', 'hr_manager'].includes(user?.role ?? '');

  const [sessions, setSessions] = useState<Session[]>([]);
  const [selectedSession, setSelectedSession] = useState<Session | null>(null);
  const [entries, setEntries] = useState<Entry[]>([]);
  const [distribution, setDistribution] = useState<Distribution | null>(null);
  const [employees, setEmployees] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tab, setTab] = useState(0);

  const [sessionDialog, setSessionDialog] = useState<Partial<Session> | null>(null);
  const [entryDialog, setEntryDialog] = useState<Partial<Entry> | null>(null);
  const [expandedEntries, setExpandedEntries] = useState<Set<string>>(new Set());

  const today = new Date().toISOString().slice(0, 10);

  const loadSessions = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const s = await apiFetch('/api/calibration/sessions');
      setSessions(s);
    } catch (e: any) { setError(e.message); }
    setLoading(false);
  }, []);

  const loadSession = useCallback(async (id: string) => {
    setLoading(true);
    try {
      const [s, e, d] = await Promise.all([
        apiFetch(`/api/calibration/sessions/${id}`),
        apiFetch(`/api/calibration/sessions/${id}/entries`),
        apiFetch(`/api/calibration/sessions/${id}/distribution`),
      ]);
      setSelectedSession(s); setEntries(e); setDistribution(d);
    } catch (e: any) { setError((e as any).message); }
    setLoading(false);
  }, []);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  useEffect(() => {
    if (employees.length === 0)
      apiFetch('/api/employees?limit=500').then(r => setEmployees(r.data || r || [])).catch(() => {});
  }, [employees.length]);

  const selectSession = (s: Session) => { setSelectedSession(s); setTab(1); loadSession(s.id); };
  const back = () => { setSelectedSession(null); setTab(0); loadSessions(); };

  const saveSession = async () => {
    if (!sessionDialog) return;
    try {
      sessionDialog.id
        ? await apiFetch(`/api/calibration/sessions/${sessionDialog.id}`, { method: 'PATCH', body: JSON.stringify(sessionDialog) })
        : await apiFetch('/api/calibration/sessions', { method: 'POST', body: JSON.stringify(sessionDialog) });
      setSessionDialog(null); loadSessions();
    } catch (e: any) { setError(e.message); }
  };

  const deleteSession = async (id: string) => {
    if (!window.confirm('Delete this calibration session?')) return;
    try { await apiFetch(`/api/calibration/sessions/${id}`, { method: 'DELETE' }); back(); }
    catch (e: any) { setError(e.message); }
  };

  const startReview = async (id: string) => {
    try { await apiFetch(`/api/calibration/sessions/${id}/start-review`, { method: 'POST' }); loadSession(id); }
    catch (e: any) { setError(e.message); }
  };

  const lockSession = async (id: string) => {
    if (!window.confirm('Lock this session? All entries will be finalised and cannot be edited.')) return;
    try { await apiFetch(`/api/calibration/sessions/${id}/lock`, { method: 'POST' }); loadSession(id); }
    catch (e: any) { setError(e.message); }
  };

  const saveEntry = async () => {
    if (!entryDialog || !selectedSession) return;
    try {
      if (entryDialog.id) {
        await apiFetch(`/api/calibration/entries/${entryDialog.id}`, { method: 'PATCH', body: JSON.stringify(entryDialog) });
      } else {
        await apiFetch(`/api/calibration/sessions/${selectedSession.id}/entries`, { method: 'POST', body: JSON.stringify(entryDialog) });
      }
      setEntryDialog(null); loadSession(selectedSession.id);
    } catch (e: any) { setError(e.message); }
  };

  const deleteEntry = async (id: string) => {
    if (!window.confirm('Remove this entry?')) return;
    try { await apiFetch(`/api/calibration/entries/${id}`, { method: 'DELETE' }); selectedSession && loadSession(selectedSession.id); }
    catch (e: any) { setError(e.message); }
  };

  const toggleExpand = (id: string) => {
    setExpandedEntries(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });
  };

  const isLocked = selectedSession?.status === 'locked';

  // ── Session List ──────────────────────────────────────────────────────────

  if (!selectedSession) return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tune sx={{ fontSize: 32, color: '#4527a0' }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>Performance Calibration</Typography>
            <Typography variant="body2" color="text.secondary">Cross-manager sessions to align ratings & salary decisions</Typography>
          </Box>
        </Box>
        {isHR && (
          <Button variant="contained" startIcon={<Add />} sx={{ bgcolor: '#4527a0' }}
            onClick={() => setSessionDialog({ cycle: 'year_end', year: new Date().getFullYear(), targetExceptional: 5, targetExceeds: 20, targetMeets: 60, targetBelowExpectations: 10, targetUnsatisfactory: 5, openDate: today })}>
            New Session
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {sessions.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center', color: 'text.secondary' }}>
          <Tune sx={{ fontSize: 56, mb: 1, opacity: 0.2 }} />
          <Typography>No calibration sessions yet. Create one to begin the year-end rating alignment process.</Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {sessions.map(s => {
            const entryCount = s.entries?.length ?? 0;
            const locked = s.status === 'locked';
            return (
              <Grid key={s.id} size={{ xs: 12, sm: 6, md: 4 }}>
                <Card sx={{ cursor: 'pointer', border: `2px solid ${locked ? '#e0e0e0' : '#e8d5ff'}`, '&:hover': { boxShadow: 4 } }}
                  onClick={() => selectSession(s)}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Chip label={STATUS_LABELS[s.status]} size="small" color={STATUS_COLORS[s.status] ?? 'default'} />
                      <Chip label={CYCLES[s.cycle] ?? s.cycle} size="small" variant="outlined" />
                    </Box>
                    <Typography variant="h6" fontWeight={700} gutterBottom>{s.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {s.year} · {s.department ?? 'Company-wide'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                      {fmtDate(s.openDate)} → {fmtDate(s.closeDate)}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 1.5, alignItems: 'center' }}>
                      <People fontSize="small" sx={{ color: '#90a4ae' }} />
                      <Typography variant="body2">{entryCount} employee{entryCount !== 1 ? 's' : ''}</Typography>
                      {isHR && !locked && (
                        <Box sx={{ ml: 'auto' }}>
                          <IconButton size="small" onClick={e => { e.stopPropagation(); setSessionDialog({ ...s }); }}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* New / Edit Session Dialog */}
      <Dialog open={!!sessionDialog} onClose={() => setSessionDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {sessionDialog?.id ? 'Edit Session' : 'New Calibration Session'}
          <IconButton sx={{ position: 'absolute', right: 8, top: 8 }} onClick={() => setSessionDialog(null)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField label="Session Name" fullWidth required value={sessionDialog?.name ?? ''}
              onChange={e => setSessionDialog(p => ({ ...p, name: e.target.value }))}
              placeholder="e.g. FY2025 Year-End Calibration" />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField select label="Cycle" fullWidth value={sessionDialog?.cycle ?? 'year_end'}
                onChange={e => setSessionDialog(p => ({ ...p, cycle: e.target.value }))}>
                {Object.entries(CYCLES).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
              </TextField>
              <TextField label="Year" type="number" sx={{ width: 120 }} value={sessionDialog?.year ?? new Date().getFullYear()}
                onChange={e => setSessionDialog(p => ({ ...p, year: Number(e.target.value) }))} />
            </Box>
            <TextField label="Department (blank = company-wide)" fullWidth value={sessionDialog?.department ?? ''}
              onChange={e => setSessionDialog(p => ({ ...p, department: e.target.value || undefined }))} />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Open Date" type="date" fullWidth InputLabelProps={{ shrink: true }} value={sessionDialog?.openDate ?? today}
                onChange={e => setSessionDialog(p => ({ ...p, openDate: e.target.value }))} />
              <TextField label="Close Date" type="date" fullWidth InputLabelProps={{ shrink: true }} value={sessionDialog?.closeDate ?? ''}
                onChange={e => setSessionDialog(p => ({ ...p, closeDate: e.target.value }))} />
            </Box>
            <Typography variant="subtitle2" fontWeight={700}>Target Rating Distribution (%)</Typography>
            <Grid container spacing={1}>
              {['targetExceptional', 'targetExceeds', 'targetMeets', 'targetBelowExpectations', 'targetUnsatisfactory'].map(k => (
                <Grid key={k} size={{ xs: 6, sm: 4 }}>
                  <TextField label={RATINGS[k.replace('target', '').replace(/([A-Z])/g, m => '_' + m.toLowerCase()).slice(1)]?.label ?? k}
                    type="number" fullWidth size="small"
                    value={(sessionDialog as any)?.[k] ?? 0}
                    onChange={e => setSessionDialog(p => ({ ...p, [k]: Number(e.target.value) }))} />
                </Grid>
              ))}
            </Grid>
            <TextField label="Facilitator Notes" fullWidth multiline rows={2} value={sessionDialog?.facilitatorNotes ?? ''}
              onChange={e => setSessionDialog(p => ({ ...p, facilitatorNotes: e.target.value }))} />
          </Box>
        </DialogContent>
        <DialogActions>
          {sessionDialog?.id && <Button color="error" onClick={() => deleteSession(sessionDialog.id!)}>Delete</Button>}
          <Button onClick={() => setSessionDialog(null)}>Cancel</Button>
          <Button variant="contained" sx={{ bgcolor: '#4527a0' }} onClick={saveSession} disabled={!sessionDialog?.name}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

  // ── Session Detail View ───────────────────────────────────────────────────

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button variant="text" onClick={back} sx={{ color: '#4527a0' }}>← Sessions</Button>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={700}>{selectedSession.name}</Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
            <Chip label={STATUS_LABELS[selectedSession.status]} size="small" color={STATUS_COLORS[selectedSession.status] ?? 'default'} />
            <Chip label={CYCLES[selectedSession.cycle] ?? selectedSession.cycle} size="small" variant="outlined" />
            {selectedSession.department && <Chip label={selectedSession.department} size="small" />}
          </Box>
        </Box>
        {isHR && !isLocked && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            {selectedSession.status === 'open' && (
              <Button variant="outlined" startIcon={<PlayArrow />} onClick={() => startReview(selectedSession.id)}>Start Review</Button>
            )}
            {selectedSession.status === 'in_review' && (
              <Button variant="contained" color="warning" startIcon={<Lock />} onClick={() => lockSession(selectedSession.id)}>Lock Session</Button>
            )}
            <Button variant="outlined" startIcon={<Add />} onClick={() => setEntryDialog({ sessionId: selectedSession.id, isHighPotential: false, isAtRisk: false, isSuccessionCandidate: false })}>
              Add Employee
            </Button>
          </Box>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {isLocked && <Alert severity="info" icon={<Lock />} sx={{ mb: 2 }}>Session locked on {selectedSession.lockedAt ? new Date(selectedSession.lockedAt).toLocaleDateString('en-HK') : '—'}. Ratings are final.</Alert>}

      {/* Stats row */}
      {distribution && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Card sx={{ textAlign: 'center', p: 1 }}>
              <CardContent>
                <Typography variant="h4" fontWeight={700} color="#4527a0">{distribution.total}</Typography>
                <Typography variant="body2" color="text.secondary">Employees</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Card sx={{ textAlign: 'center', p: 1 }}>
              <CardContent>
                <Typography variant="h4" fontWeight={700} color="#1565c0">{distribution.highPotential}</Typography>
                <Typography variant="body2" color="text.secondary">High Potential</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Card sx={{ textAlign: 'center', p: 1 }}>
              <CardContent>
                <Typography variant="h4" fontWeight={700} color="#c62828">{distribution.atRisk}</Typography>
                <Typography variant="body2" color="text.secondary">At Risk</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Card sx={{ textAlign: 'center', p: 1 }}>
              <CardContent>
                <Typography variant="h4" fontWeight={700} color="#2e7d32">{distribution.avgIncreasePercent}%</Typography>
                <Typography variant="body2" color="text.secondary">Avg Increase</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label="Rating Grid" />
          <Tab label="Distribution" />
        </Tabs>
      </Paper>

      {/* ── RATING GRID ── */}
      {tab === 1 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell><b>Employee</b></TableCell>
                <TableCell><b>Dept</b></TableCell>
                <TableCell><b>Manager Rating</b></TableCell>
                <TableCell><b>Calibrated Rating</b></TableCell>
                <TableCell><b>Proposed +%</b></TableCell>
                <TableCell><b>Approved +%</b></TableCell>
                <TableCell><b>Flags</b></TableCell>
                {!isLocked && <TableCell><b>Actions</b></TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.length === 0 ? (
                <TableRow><TableCell colSpan={8} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>No employees added yet.</TableCell></TableRow>
              ) : entries.map(e => (
                <React.Fragment key={e.id}>
                  <TableRow hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <IconButton size="small" onClick={() => toggleExpand(e.id)}>
                          {expandedEntries.has(e.id) ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                        </IconButton>
                        <Box>
                          <Typography fontWeight={600}>{e.employee?.firstName} {e.employee?.lastName}</Typography>
                          <Typography variant="caption" color="text.secondary">{e.employee?.position}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{e.employee?.department?.name ?? '—'}</TableCell>
                    <TableCell><RatingChip rating={e.managerRating} /></TableCell>
                    <TableCell><RatingChip rating={e.calibratedRating} /></TableCell>
                    <TableCell>{e.proposedIncreasePercent != null ? `${e.proposedIncreasePercent}%` : '—'}</TableCell>
                    <TableCell>
                      {e.approvedIncreasePercent != null
                        ? <Typography fontWeight={700} color="#2e7d32">{e.approvedIncreasePercent}%</Typography>
                        : '—'}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {e.isHighPotential && <Chip label="HiPo" size="small" color="primary" sx={{ fontSize: '0.65rem' }} />}
                        {e.isAtRisk && <Chip label="At Risk" size="small" color="error" sx={{ fontSize: '0.65rem' }} />}
                        {e.isSuccessionCandidate && <Chip label="Succession" size="small" color="warning" sx={{ fontSize: '0.65rem' }} />}
                      </Box>
                    </TableCell>
                    {!isLocked && (
                      <TableCell>
                        <Tooltip title="Edit"><IconButton size="small" onClick={() => setEntryDialog({ ...e })}><Edit fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Remove"><IconButton size="small" color="error" onClick={() => deleteEntry(e.id)}><Delete fontSize="small" /></IconButton></Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={isLocked ? 7 : 8} sx={{ p: 0 }}>
                      <Collapse in={expandedEntries.has(e.id)}>
                        <Box sx={{ p: 2, bgcolor: '#fafafa', borderLeft: '3px solid #e8d5ff' }}>
                          {e.managerJustification && (
                            <Typography variant="body2"><b>Manager justification:</b> {e.managerJustification}</Typography>
                          )}
                          {e.discussionNotes && (
                            <Typography variant="body2" sx={{ mt: 0.5 }}><b>Calibration notes:</b> {e.discussionNotes}</Typography>
                          )}
                          {e.bonusMultiplier != null && (
                            <Typography variant="body2" sx={{ mt: 0.5 }}><b>Bonus multiplier:</b> {e.bonusMultiplier}×</Typography>
                          )}
                          {!e.managerJustification && !e.discussionNotes && !e.bonusMultiplier && (
                            <Typography variant="body2" color="text.secondary">No notes recorded.</Typography>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ── DISTRIBUTION ── */}
      {tab === 2 && distribution && (
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>Rating Distribution vs Target</Typography>
              {RATING_ORDER.map(r => (
                <DistributionBar
                  key={r} rating={r}
                  count={distribution.byRating[r]?.count ?? 0}
                  actual={distribution.byRating[r]?.percent ?? 0}
                  target={distribution.targetMap[r] ?? 0}
                  total={distribution.total} />
              ))}
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>Talent Flags</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {[
                  { label: 'High Potential', count: distribution.highPotential, color: '#1565c0', pct: distribution.total > 0 ? Math.round((distribution.highPotential / distribution.total) * 100) : 0 },
                  { label: 'At Risk', count: distribution.atRisk, color: '#c62828', pct: distribution.total > 0 ? Math.round((distribution.atRisk / distribution.total) * 100) : 0 },
                  { label: 'Succession Candidates', count: distribution.successionCandidates, color: '#e65100', pct: distribution.total > 0 ? Math.round((distribution.successionCandidates / distribution.total) * 100) : 0 },
                ].map(f => (
                  <Box key={f.label}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" fontWeight={600}>{f.label}</Typography>
                      <Typography variant="body2">{f.count} ({f.pct}%)</Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={f.pct} sx={{ height: 8, borderRadius: 4, mt: 0.5, '& .MuiLinearProgress-bar': { bgcolor: f.color } }} />
                  </Box>
                ))}
              </Box>
              <Box sx={{ mt: 3, p: 2, bgcolor: '#f9f0ff', borderRadius: 2 }}>
                <Typography variant="subtitle2" fontWeight={700}>Compensation Summary</Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>Average approved increase: <b>{distribution.avgIncreasePercent}%</b></Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* ── ENTRY DIALOG ── */}
      <Dialog open={!!entryDialog} onClose={() => setEntryDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {entryDialog?.id ? 'Edit Entry' : 'Add Employee to Calibration'}
          <IconButton sx={{ position: 'absolute', right: 8, top: 8 }} onClick={() => setEntryDialog(null)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {!entryDialog?.id && (
              <TextField select label="Employee" fullWidth required value={entryDialog?.employeeId ?? ''}
                onChange={e => setEntryDialog(p => ({ ...p, employeeId: e.target.value }))}>
                <MenuItem value="">— Select employee —</MenuItem>
                {employees.map((emp: any) => <MenuItem key={emp.id} value={emp.id}>{emp.firstName} {emp.lastName}{emp.department?.name ? ` (${emp.department.name})` : ''}</MenuItem>)}
              </TextField>
            )}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField select label="Manager Rating" fullWidth value={entryDialog?.managerRating ?? ''}
                onChange={e => setEntryDialog(p => ({ ...p, managerRating: e.target.value as any }))}>
                <MenuItem value="">— Not set —</MenuItem>
                {RATING_ORDER.map(r => <MenuItem key={r} value={r}>{RATINGS[r].label}</MenuItem>)}
              </TextField>
              <TextField select label="Calibrated Rating" fullWidth value={entryDialog?.calibratedRating ?? ''}
                onChange={e => setEntryDialog(p => ({ ...p, calibratedRating: e.target.value as any }))}>
                <MenuItem value="">— Not set —</MenuItem>
                {RATING_ORDER.map(r => <MenuItem key={r} value={r}>{RATINGS[r].label}</MenuItem>)}
              </TextField>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Proposed Increase %" type="number" fullWidth
                value={entryDialog?.proposedIncreasePercent ?? ''}
                onChange={e => setEntryDialog(p => ({ ...p, proposedIncreasePercent: e.target.value ? Number(e.target.value) : undefined }))} />
              <TextField label="Approved Increase %" type="number" fullWidth
                value={entryDialog?.approvedIncreasePercent ?? ''}
                onChange={e => setEntryDialog(p => ({ ...p, approvedIncreasePercent: e.target.value ? Number(e.target.value) : undefined }))} />
              <TextField label="Bonus Multiplier" type="number" fullWidth
                value={entryDialog?.bonusMultiplier ?? ''}
                onChange={e => setEntryDialog(p => ({ ...p, bonusMultiplier: e.target.value ? Number(e.target.value) : undefined }))}
                placeholder="e.g. 1.2" />
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <FormControlLabel control={<Switch checked={entryDialog?.isHighPotential ?? false}
                onChange={e => setEntryDialog(p => ({ ...p, isHighPotential: e.target.checked }))} />} label="High Potential" />
              <FormControlLabel control={<Switch checked={entryDialog?.isAtRisk ?? false}
                onChange={e => setEntryDialog(p => ({ ...p, isAtRisk: e.target.checked }))} />} label="At Risk" />
              <FormControlLabel control={<Switch checked={entryDialog?.isSuccessionCandidate ?? false}
                onChange={e => setEntryDialog(p => ({ ...p, isSuccessionCandidate: e.target.checked }))} />} label="Succession Candidate" />
            </Box>
            <TextField label="Manager Justification" fullWidth multiline rows={2}
              value={entryDialog?.managerJustification ?? ''}
              onChange={e => setEntryDialog(p => ({ ...p, managerJustification: e.target.value }))} />
            <TextField label="Calibration Discussion Notes (HR only)" fullWidth multiline rows={2}
              value={entryDialog?.discussionNotes ?? ''}
              onChange={e => setEntryDialog(p => ({ ...p, discussionNotes: e.target.value }))} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEntryDialog(null)}>Cancel</Button>
          <Button variant="contained" sx={{ bgcolor: '#4527a0' }} onClick={saveEntry}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
